'use client';

import Link from 'next/link';
import Image from 'next/image';
import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { isServicesPublic } from '@/lib/featureFlags';
import {
  EVENT_CATEGORIES,
  normalizeEventCategory,
} from '@/lib/eventCategories';
import { LogoPlaceholder } from '@/components/LogoPlaceholder';
import { focalToObjectPosition } from '@/lib/imageFocal';
import { isPaidMembershipType } from '@/lib/memberPosts';

const nav = [
  { href: '/', label: 'ホーム' },
  { href: '/about', label: '結会とは' },
  { href: '/events', label: '各種イベント' },
  ...(isServicesPublic() ? [{ href: '/services', label: 'サービス' }] : []),
  { href: '/market', label: '結び市' },
  { href: '/members', label: '会員紹介' },
  { href: '/testimonials', label: '会員様の声' },
  { href: '/clubs', label: '部活' },
  { href: '/recruitment', label: '求人サポート' },
  { href: '/interview', label: '動画取材「さなえが行く」' },
  { href: '/contact', label: 'お問い合わせ' },
  { href: '/register', label: '会員申込' },
  { href: '/blog', label: 'ブログ' },
];

export function Header() {
  const pathname = usePathname();
  const sp = useSearchParams();
  const [open, setOpen] = useState(false);
  const [eventsDropdownOpen, setEventsDropdownOpen] = useState(false);
  const [eventsSidebarOpen, setEventsSidebarOpen] = useState(false);
  const [eventsMobileOpen, setEventsMobileOpen] = useState(false);
  const [marketSidebarOpen, setMarketSidebarOpen] = useState(false);
  const [marketMobileOpen, setMarketMobileOpen] = useState(false);
  const { user, logout, loading } = useAuth();
  // 目的: ログイン中は「会員申込」などのCTA導線を非表示にする
  const navItems = user ? nav.filter((n) => n.href !== '/register') : nav;
  // 目的: 仕様 4.1「ヘッダーの会員メニューに『ブログを書く』の導線を足す。無料会員には出さない」
  // 判定は isPaidMembershipType に集約する（allowlist なので種別が null・未知のときは出さない。
  // ここで === 'paid_female' などと書き直さない）。認証の復元中（loading）は、一瞬出てから
  // 消えるのを避けるため出さない
  const canWriteBlog = !loading && isPaidMembershipType(user?.membershipType);
  const activeCategory = normalizeEventCategory(sp.get('category'));

  // 目的: 「ログイン/会員申込」CTAの見た目を全ヘッダーで統一する
  const ctaPrimary =
    'inline-flex items-center justify-center rounded-full bg-linear-to-r from-primary to-primary/60 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:from-primary/90 hover:to-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white';
  const ctaSecondary =
    'inline-flex items-center justify-center rounded-full border border-gray-200 bg-white/70 px-5 py-2.5 text-sm font-bold text-gray-800 shadow-sm transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white';

  // 目的: 現在地（アクティブ）を一瞬で判別できるようにする
  const isActive = (href: string) =>
    href === '/' ? pathname === '/' : pathname?.startsWith(href);

  // 目的: 1200px以上ではサイドバー表示に合わせ、bodyに左余白を付ける
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1200px)');
    const apply = () => {
      document.documentElement.classList.toggle(
        'has-site-sidebar',
        mql.matches,
      );
    };
    apply();

    // Safari互換
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', apply);
      return () => {
        mql.removeEventListener('change', apply);
        document.documentElement.classList.remove('has-site-sidebar');
      };
    }
    // Safari など一部環境向けの後方互換: 古い MediaQueryList API
    mql.addListener(apply);
    return () => {
      mql.removeListener(apply);
      document.documentElement.classList.remove('has-site-sidebar');
    };
  }, []);

  // ルート遷移時やEscapeでクローズ
  useEffect(() => {
    setTimeout(() => {
      setOpen(false);
      setEventsDropdownOpen(false);
      setEventsSidebarOpen(false);
      setEventsMobileOpen(false);
      setMarketMobileOpen(false);
      if (pathname?.startsWith('/market')) {
        setMarketSidebarOpen(true);
      }
    }, 0);
  }, [pathname]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setEventsDropdownOpen(false);
        setEventsSidebarOpen(false);
        setEventsMobileOpen(false);
        setMarketSidebarOpen(false);
        setMarketMobileOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // 目的: モバイルメニュー表示中は背面スクロールを止め、誤操作を減らす
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  return (
    <>
      {/* 1200px以上: サイドバー */}
      <aside className='hidden min-[1200px]:flex fixed left-0 top-0 z-50 h-dvh w-[280px] flex-col border-r border-gray-100/60 bg-yui-gradient'>
        <div className='px-6 py-5'>
          <Link href='/' className='flex items-center gap-3'>
            <Image
              src='/assets/img/logo-yuikai.png'
              alt='Yui Kai'
              width={69}
              height={72}
              priority
              quality={75}
              className='h-14 w-14'
            />
            <div className='leading-tight'>
              <p className='font-serif text-lg text-gray-800'>結会</p>
              <p className='text-xs text-gray-600'>Yui Kai</p>
            </div>
          </Link>
        </div>

        <nav className='flex-1 px-6 overflow-y-auto'>
          <ul className='space-y-0.5 pb-2'>
            {navItems.map((item) => {
              const active = isActive(item.href);
              if (item.href === '/events') {
                return (
                  <li key={item.href}>
                    <button
                      type='button'
                      aria-expanded={eventsSidebarOpen}
                      aria-controls='sidebar-events-submenu'
                      onClick={() => setEventsSidebarOpen((v) => !v)}
                      className={`relative flex w-full items-center justify-between rounded-md px-3 py-2.5 text-left text-[14px] tracking-tight text-gray-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                        active
                          ? 'bg-white/85 text-gray-900 font-semibold'
                          : 'hover:bg-white/60'
                      }`}
                    >
                      <span
                        aria-hidden='true'
                        className={`absolute left-1 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full bg-primary transition-opacity ${
                          active ? 'opacity-100' : 'opacity-0'
                        }`}
                      />
                      <span className='font-serif'>{item.label}</span>
                      <span
                        aria-hidden='true'
                        className={`ml-2 inline-flex size-6 items-center justify-center rounded-full bg-white/70 text-gray-700 transition-transform ${
                          eventsSidebarOpen ? 'rotate-180' : 'rotate-0'
                        }`}
                      >
                        <svg
                          width='14'
                          height='14'
                          viewBox='0 0 24 24'
                          fill='none'
                          xmlns='http://www.w3.org/2000/svg'
                        >
                          <path
                            d='M6 9l6 6 6-6'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      </span>
                    </button>
                    <ul
                      id='sidebar-events-submenu'
                      aria-hidden={!eventsSidebarOpen}
                      className={`mt-1 space-y-0.5 pl-4 overflow-hidden transition-[max-height,opacity] duration-300 ease-out motion-reduce:transition-none ${
                        eventsSidebarOpen
                          ? 'max-h-[480px] opacity-100 visible'
                          : 'max-h-0 opacity-0 invisible'
                      }`}
                    >
                      <li>
                        <Link
                          href='/events'
                          className={`block rounded-md px-3 py-2 text-[13px] text-gray-700 transition-colors hover:bg-white/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                            pathname === '/events' && !activeCategory
                              ? 'bg-white/85 font-semibold text-gray-900'
                              : ''
                          }`}
                        >
                          すべて
                        </Link>
                      </li>
                      {EVENT_CATEGORIES.map((c) => (
                        <li key={c}>
                          <Link
                            href={{
                              pathname: '/events',
                              query: { category: c },
                            }}
                            className={`block rounded-md px-3 py-2 text-[13px] text-gray-700 transition-colors hover:bg-white/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                              pathname === '/events' && activeCategory === c
                                ? 'bg-white/85 font-semibold text-gray-900'
                                : ''
                            }`}
                          >
                            {c}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              }
              if (item.href === '/market') {
                return (
                  <li key={item.href}>
                    <button
                      type='button'
                      aria-expanded={marketSidebarOpen}
                      aria-controls='sidebar-market-submenu'
                      onClick={() => setMarketSidebarOpen((v) => !v)}
                      className={`relative flex w-full items-center justify-between rounded-md px-3 py-2.5 text-left text-[14px] tracking-tight text-gray-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                        active
                          ? 'bg-white/85 text-gray-900 font-semibold'
                          : 'hover:bg-white/60'
                      }`}
                    >
                      <span
                        aria-hidden='true'
                        className={`absolute left-1 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full bg-primary transition-opacity ${
                          active ? 'opacity-100' : 'opacity-0'
                        }`}
                      />
                      <span className='font-serif'>{item.label}</span>
                      <span
                        aria-hidden='true'
                        className={`ml-2 inline-flex size-6 items-center justify-center rounded-full bg-white/70 text-gray-700 transition-transform ${
                          marketSidebarOpen ? 'rotate-180' : 'rotate-0'
                        }`}
                      >
                        <svg
                          width='14'
                          height='14'
                          viewBox='0 0 24 24'
                          fill='none'
                          xmlns='http://www.w3.org/2000/svg'
                        >
                          <path
                            d='M6 9l6 6 6-6'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      </span>
                    </button>
                    <ul
                      id='sidebar-market-submenu'
                      aria-hidden={!marketSidebarOpen}
                      className={`mt-1 space-y-0.5 pl-4 overflow-hidden transition-[max-height,opacity] duration-300 ease-out motion-reduce:transition-none ${
                        marketSidebarOpen
                          ? 'max-h-[320px] opacity-100 visible'
                          : 'max-h-0 opacity-0 invisible'
                      }`}
                    >
                      {[
                        { href: '/market', label: '結び市トップ' },
                        { href: '/market/shops', label: 'ショップ一覧' },
                        { href: '/market/my-shop', label: 'マイショップ' },
                        { href: '/market/cart', label: 'カート' },
                        { href: '/market/my-orders', label: '注文履歴' },
                        {
                          href: '/market/shop/apply',
                          label: 'ショップ開設申請',
                        },
                      ].map((sub) => (
                        <li key={sub.href}>
                          <Link
                            href={sub.href}
                            className={`block rounded-md px-3 py-2 text-[13px] text-gray-700 transition-colors hover:bg-white/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                              pathname === sub.href
                                ? 'bg-white/85 font-semibold text-gray-900'
                                : ''
                            }`}
                          >
                            {sub.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              }
              return (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    aria-current={active ? 'page' : undefined}
                    className={`relative block rounded-md px-3 py-2.5 text-[14px] tracking-tight text-gray-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                      active
                        ? 'bg-white/85 text-gray-900 font-semibold'
                        : 'hover:bg-white/60'
                    }`}
                  >
                    {/* 左アクセント（現在地） */}
                    <span
                      aria-hidden='true'
                      className={`absolute left-1 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full bg-primary transition-opacity ${
                        active ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                    <span className='font-serif'>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className='border-t border-gray-200/70 px-6 py-4'>
          {user ? (
            <div className='space-y-3'>
              <Link
                href='/profile'
                className='flex items-center gap-3 rounded bg-white/70 px-3 py-2 hover:bg-white'
              >
                <div className='relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-yui-muted text-primary border border-primary/20'>
                  {user.avatarUrl ? (
                    <Image
                      src={user.avatarUrl}
                      alt='プロフィール画像'
                      fill
                      className='object-cover'
                      style={{ objectPosition: focalToObjectPosition(user.photoFocal, 'center') }}
                      sizes='36px'
                    />
                  ) : (
                    <LogoPlaceholder size={22} className='opacity-80' />
                  )}
                </div>
                <div className='min-w-0'>
                  <p className='truncate text-sm font-bold text-gray-800'>
                    {user.displayName}
                  </p>
                  <p className='truncate text-xs text-gray-500'>{user.email}</p>
                </div>
              </Link>
              {/* 目的: 名前のカード自体もマイページへ行くが、スマホのメニューと同じく
                  「マイページ」と書いたボタンを出して、PCでも行き先が分かるようにする */}
              <Link
                href='/profile'
                className='block w-full rounded border border-gray-200 bg-white/70 px-4 py-2 text-center text-sm font-bold text-gray-800 hover:bg-white'
              >
                マイページ
              </Link>
              {canWriteBlog && (
                <Link
                  href='/profile/posts'
                  className='block w-full rounded border border-gray-200 bg-white/70 px-4 py-2 text-center text-sm font-bold text-gray-800 hover:bg-white'
                >
                  ブログを書く
                </Link>
              )}
              <button
                onClick={() => logout()}
                className='w-full rounded bg-gray-100 px-4 py-2 text-center text-sm font-bold text-gray-600 hover:bg-gray-200'
              >
                ログアウト
              </button>
            </div>
          ) : (
            <div className='flex flex-col gap-3'>
              <Link href='/register' className={ctaPrimary}>
                会員申込
              </Link>
              <Link href='/login' className={ctaSecondary}>
                ログイン
              </Link>
            </div>
          )}
        </div>
      </aside>

      {/* 1200px未満: トップヘッダー */}
      <header
        data-header
        className='min-[1200px]:hidden sticky top-0 z-50 w-full border-b border-gray-100/60 bg-yui-gradient shadow-sm'
      >
        <div className='mx-auto flex h-20 w-full max-w-7xl items-center gap-4 px-4 py-2 sm:px-6'>
          <Link href='/' className='flex shrink-0 items-center gap-3'>
            <Image
              src='/assets/img/logo-yuikai.png'
              alt='Yui Kai'
              width={69}
              height={72}
              priority
              quality={75}
              className='h-15 w-14 object-cover md:h-16 md:w-[60px]'
            />
          </Link>
          {/* 目的: ナビが非表示の幅（〜899px）でも、右側（操作群/ハンバーガー）を右端に配置する */}
          <div className='flex-1 min-[900px]:hidden' />
          {/* 目的: 800〜1200px帯でナビが折り返して崩れないよう、表示幅に応じて段階的に出しつつ
              ナビ領域は min-w-0 で圧縮する */}
          <nav className='hidden min-w-0 flex-1 items-center gap-4 whitespace-nowrap min-[900px]:flex lg:gap-6'>
            {navItems.map((item) => {
              const active = isActive(item.href);
              if (item.href === '/events') {
                return (
                  <div key={item.href} className='relative shrink-0'>
                    <button
                      type='button'
                      className='group relative shrink-0 rounded px-1 py-1 text-[14px] tracking-tight text-gray-800 transition-colors hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white lg:text-[16px]'
                      aria-current={active ? 'page' : undefined}
                      aria-expanded={eventsDropdownOpen}
                      aria-controls='top-events-submenu'
                      onClick={() => setEventsDropdownOpen((v) => !v)}
                    >
                      <span
                        className={`font-serif ${
                          active ? 'text-gray-900 font-semibold' : ''
                        }`}
                      >
                        {item.label}
                      </span>
                      <span
                        className={`absolute -bottom-1 left-0 h-0.5 transition-all duration-200 ${
                          active
                            ? 'w-full bg-secondary'
                            : 'w-0 bg-secondary group-hover:w-full'
                        }`}
                      />
                    </button>
                    <div
                      className={`${
                        eventsDropdownOpen ? 'block' : 'hidden'
                      } absolute left-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg`}
                      id='top-events-submenu'
                    >
                      <div className='p-2'>
                        <Link
                          href='/events'
                          className={`block rounded-lg px-3 py-2 text-sm text-gray-800 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                            pathname === '/events' && !activeCategory
                              ? 'bg-gray-50 font-semibold'
                              : ''
                          }`}
                          onClick={() => setEventsDropdownOpen(false)}
                        >
                          すべて
                        </Link>
                        {EVENT_CATEGORIES.map((c) => (
                          <Link
                            key={c}
                            href={{
                              pathname: '/events',
                              query: { category: c },
                            }}
                            className={`mt-1 block rounded-lg px-3 py-2 text-sm text-gray-800 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                              pathname === '/events' && activeCategory === c
                                ? 'bg-gray-50 font-semibold'
                                : ''
                            }`}
                            onClick={() => setEventsDropdownOpen(false)}
                          >
                            {c}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className='group relative shrink-0 rounded px-1 py-1 text-[14px] tracking-tight text-gray-800 transition-colors hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white lg:text-[16px]'
                >
                  <span
                    className={`font-serif ${
                      active ? 'text-gray-900 font-semibold' : ''
                    }`}
                  >
                    {item.label}
                  </span>
                  <span
                    className={`absolute -bottom-1 left-0 h-0.5 transition-all duration-200 ${
                      active
                        ? 'w-full bg-secondary'
                        : 'w-0 bg-secondary group-hover:w-full'
                    }`}
                  />
                </Link>
              );
            })}
          </nav>
          <div className='flex shrink-0 items-center gap-3 sm:gap-4'>
            {user ? (
              <Link href='/profile' className='hidden min-[1100px]:block'>
                <div className='relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-yui-muted text-primary border border-primary/20 hover:bg-primary hover:text-white transition-colors'>
                  {user.avatarUrl ? (
                    <Image
                      src={user.avatarUrl}
                      alt='プロフィール画像'
                      fill
                      className='object-cover'
                      style={{ objectPosition: focalToObjectPosition(user.photoFocal, 'center') }}
                      sizes='40px'
                    />
                  ) : (
                    <LogoPlaceholder size={24} className='opacity-80' />
                  )}
                </div>
              </Link>
            ) : (
              <>
                <Link
                  href='/register'
                  className={`hidden min-[1100px]:inline-flex ${ctaPrimary}`}
                >
                  会員申込
                </Link>
                <Link
                  href='/login'
                  className={`hidden min-[1100px]:inline-flex ${ctaSecondary}`}
                >
                  ログイン
                </Link>
              </>
            )}
            <button
              type='button'
              aria-label={open ? 'メニューを閉じる' : 'メニューを開く'}
              aria-expanded={open}
              aria-controls='mobile-nav'
              onClick={() => setOpen((v) => !v)}
              className='inline-flex h-11 w-11 items-center justify-center rounded-full border border-gray-200/70 bg-white/70 text-gray-700 shadow-sm transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white min-[1100px]:hidden'
            >
              {open ? (
                <svg
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M6 6l12 12'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                  />
                  <path
                    d='M18 6l-12 12'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                  />
                </svg>
              ) : (
                <svg
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M4 6h16M4 12h16M4 18h16'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* 背景オーバーレイ（タップで閉じる） */}
        <div
          aria-hidden='true'
          onClick={() => setOpen(false)}
          className={`${
            open
              ? 'pointer-events-auto opacity-100'
              : 'pointer-events-none opacity-0'
          } fixed inset-0 z-40 bg-black/20 transition-opacity duration-200 min-[1100px]:hidden`}
        />

        {/* Mobile Menu */}
        <div
          id='mobile-nav'
          className={`${
            open
              ? 'pointer-events-auto opacity-100'
              : 'pointer-events-none opacity-0'
          } fixed inset-x-0 top-20 bottom-0 z-50 min-[1100px]:hidden overflow-y-auto overscroll-contain transition-[opacity,transform] duration-200 ${
            open ? 'translate-y-0' : '-translate-y-2'
          }`}
        >
          {/* 目的: 画面高が低い端末でも下が切れないよう、メニュー領域をスクロール可能にする（iPhoneのセーフエリアも考慮） */}
          <div className='mx-auto w-full max-w-7xl px-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]'>
            <div className='overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-lg'>
              <nav className='flex flex-col divide-y divide-gray-100'>
                {navItems.map((item) => {
                  const active =
                    item.href === '/'
                      ? pathname === '/'
                      : pathname?.startsWith(item.href);
                  if (item.href === '/events') {
                    return (
                      <div key={item.href} className='px-4 py-4'>
                        <button
                          type='button'
                          aria-expanded={eventsMobileOpen}
                          aria-controls='mobile-events-submenu'
                          onClick={() => setEventsMobileOpen((v) => !v)}
                          className={`flex w-full items-center justify-between text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                            active
                              ? 'text-secondary font-semibold'
                              : 'text-gray-800'
                          }`}
                        >
                          {item.label}
                          <span
                            aria-hidden='true'
                            className={`ml-2 inline-flex size-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 transition-transform ${
                              eventsMobileOpen ? 'rotate-180' : 'rotate-0'
                            }`}
                          >
                            <svg
                              width='18'
                              height='18'
                              viewBox='0 0 24 24'
                              fill='none'
                              xmlns='http://www.w3.org/2000/svg'
                            >
                              <path
                                d='M6 9l6 6 6-6'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                          </span>
                        </button>
                        <div
                          id='mobile-events-submenu'
                          className={`mt-3 grid grid-cols-2 gap-2 ${
                            eventsMobileOpen ? 'grid' : 'hidden'
                          }`}
                        >
                          <Link
                            href='/events'
                            className={`rounded-full border px-3 py-2 text-center text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                              pathname === '/events' && !activeCategory
                                ? 'border-primary bg-primary text-white'
                                : 'border-gray-200 bg-white text-gray-700'
                            }`}
                            onClick={() => setOpen(false)}
                          >
                            すべて
                          </Link>
                          {EVENT_CATEGORIES.map((c) => (
                            <Link
                              key={c}
                              href={{
                                pathname: '/events',
                                query: { category: c },
                              }}
                              className={`rounded-full border px-3 py-2 text-center text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                                pathname === '/events' && activeCategory === c
                                  ? 'border-primary bg-primary text-white'
                                  : 'border-gray-200 bg-white text-gray-700'
                              }`}
                              onClick={() => setOpen(false)}
                            >
                              {c}
                            </Link>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  if (item.href === '/market') {
                    const marketSubs = [
                      { href: '/market', label: '結び市トップ' },
                      { href: '/market/shops', label: 'ショップ一覧' },
                      { href: '/market/my-shop', label: 'マイショップ' },
                      { href: '/market/cart', label: 'カート' },
                      { href: '/market/my-orders', label: '注文履歴' },
                      { href: '/market/shop/apply', label: 'ショップ開設申請' },
                    ];
                    return (
                      <div key={item.href} className='px-4 py-4'>
                        <button
                          type='button'
                          aria-expanded={marketMobileOpen}
                          aria-controls='mobile-market-submenu'
                          onClick={() => setMarketMobileOpen((v) => !v)}
                          className={`flex w-full items-center justify-between text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                            active
                              ? 'text-secondary font-semibold'
                              : 'text-gray-800'
                          }`}
                        >
                          {item.label}
                          <span
                            aria-hidden='true'
                            className={`ml-2 inline-flex size-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 transition-transform ${
                              marketMobileOpen ? 'rotate-180' : 'rotate-0'
                            }`}
                          >
                            <svg
                              width='18'
                              height='18'
                              viewBox='0 0 24 24'
                              fill='none'
                              xmlns='http://www.w3.org/2000/svg'
                            >
                              <path
                                d='M6 9l6 6 6-6'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                          </span>
                        </button>
                        <div
                          id='mobile-market-submenu'
                          className={`mt-3 grid grid-cols-2 gap-2 ${
                            marketMobileOpen ? 'grid' : 'hidden'
                          }`}
                        >
                          {marketSubs.map((sub) => (
                            <Link
                              key={sub.href}
                              href={sub.href}
                              className={`rounded-full border px-3 py-2 text-center text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                                pathname === sub.href
                                  ? 'border-primary bg-primary text-white'
                                  : 'border-gray-200 bg-white text-gray-700'
                              }`}
                              onClick={() => setOpen(false)}
                            >
                              {sub.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={`px-4 py-4 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                        active ? 'text-secondary' : 'text-gray-800'
                      }`}
                      onClick={() => setOpen(false)}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
              {user ? (
                <div className='flex flex-col gap-3 p-4'>
                  <div className='flex items-center gap-3 border-b border-gray-100 pb-4'>
                    <div className='relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-yui-muted text-primary border border-primary/20'>
                      {user.avatarUrl ? (
                        <Image
                          src={user.avatarUrl}
                          alt='プロフィール画像'
                          fill
                          className='object-cover'
                          style={{ objectPosition: focalToObjectPosition(user.photoFocal, 'center') }}
                          sizes='40px'
                        />
                      ) : (
                        <LogoPlaceholder size={24} className='opacity-80' />
                      )}
                    </div>
                    <div className='overflow-hidden'>
                      <p className='truncate text-sm font-bold text-gray-800'>
                        {user.displayName}
                      </p>
                      <p className='truncate text-xs text-gray-500'>
                        {user.email}
                      </p>
                    </div>
                  </div>
                  <Link
                    href='/profile'
                    className='w-full border border-gray-200 px-4 py-2 text-center text-sm font-bold text-gray-800 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white'
                    onClick={() => setOpen(false)}
                  >
                    マイページ
                  </Link>
                  {canWriteBlog && (
                    <Link
                      href='/profile/posts'
                      className='w-full border border-gray-200 px-4 py-2 text-center text-sm font-bold text-gray-800 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white'
                      onClick={() => setOpen(false)}
                    >
                      ブログを書く
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      logout();
                      setOpen(false);
                    }}
                    className='w-full bg-gray-100 px-4 py-2 text-center text-sm font-bold text-gray-600 hover:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white'
                  >
                    ログアウト
                  </button>
                </div>
              ) : (
                <div className='flex items-center gap-3 p-4'>
                  <Link
                    href='/register'
                    className={`flex-1 ${ctaPrimary}`}
                    onClick={() => setOpen(false)}
                  >
                    会員申込
                  </Link>
                  <Link
                    href='/login'
                    className={`flex-1 ${ctaSecondary}`}
                    onClick={() => setOpen(false)}
                  >
                    ログイン
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
}

// 目的: useSearchParams を Suspense で囲み、静的ページ（ISR含む）のプリレンダリングを可能にする
// 備考: force-dynamic を外したことで、ビルド時にuseSearchParamsが Suspense boundary を要求するようになった
function HeaderWithSuspense() {
  return (
    <Suspense>
      <Header />
    </Suspense>
  );
}

export default HeaderWithSuspense;
